/**
 * Ряд раскрытой строки «Голоса клиента» (#2394 §9 п. 7, спека
 * docs/plans/2026-10-19-voc-trends-ui.md §3): столбцы долей по корзинам и
 * переключатель «Период» | «12 месяцев».
 *
 * - «Период» — корзины спарклайна (окно экрана). Столбец выбирает корзину, и
 *   список разговоров строки показывает только её разговоры (выбор держит
 *   `VoiceRowDetails`).
 * - «12 месяцев» — ряд по месяцам за год по конец окна, своим запросом по
 *   переключению. Месяц — K из N и «Показать этот месяц»: период страницы
 *   становится этим месяцем, и разговоры месяца — в списке строки.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VoiceTrendBucket } from '@aica/api-types/schemas';
import { Button } from '@/components/ui/button';
import { SegmentedButtons } from '@/components/ui/segmented-buttons';
import { formatNumber } from '@/lib/utils';
import { VoiceTrendBars } from './VoiceTrendBars';
import type { VoiceSearch } from './voice-search';
import { seriesRefusal, useVoiceShowMonth, useVoiceYearSeries } from './useVoiceSeries';
import {
  bucketIndex,
  bucketPeriodText,
  bucketShareText,
  rowSeries,
  seriesCoverage,
  seriesHasPoints,
  type VoiceRowSeries,
  type VoiceSeriesRef,
  type VoiceSeriesUnit,
} from './voice-series-view-model';

type TrendView = 'period' | 'year';

interface VoiceRowTrendProps {
  seriesRef: VoiceSeriesRef;
  /** K строки за окно: строка с нулём в ответе года не появится — её месяцы нули. */
  calls: number;
  series: VoiceRowSeries;
  unit: VoiceSeriesUnit;
  /** Выбранная корзина вида «Период»; `null` — весь период. */
  selected: number | null;
  onSelect: (index: number | null) => void;
  /** What «Show this month» changes besides the period (`VoiceRowDetailsTrend`). */
  monthPatch?: Partial<VoiceSearch>;
}

export function VoiceRowTrend({
  seriesRef,
  calls,
  series,
  unit,
  selected,
  onSelect,
  monthPatch,
}: VoiceRowTrendProps) {
  const { t, i18n } = useTranslation('dashboard');
  const [view, setView] = useState<TrendView>('period');
  // By the month's start, like the period's bucket (`VoiceRowDetails`): a new
  // window brings other months.
  const [monthStart, setMonthStart] = useState<string | null>(null);
  const year = useVoiceYearSeries(seriesRef, view === 'year');
  const showMonth = useVoiceShowMonth();
  // The year asks for this key alone: a row without calls in the window may
  // still have months with calls, so its row comes back like any other.
  const yearSeries = rowSeries(year.data, seriesRef, calls);
  const month = yearSeries ? bucketIndex(yearSeries.buckets, monthStart) : null;
  const period = (bucket: VoiceTrendBucket, bucketUnit: VoiceSeriesUnit) =>
    bucketPeriodText(bucket, bucketUnit, i18n.language, t);

  const changeView = (next: TrendView) => {
    setView(next);
    setMonthStart(null);
    // The period's bucket filter would hide conversations under a chart that
    // no longer shows that bucket.
    if (next === 'year') onSelect(null);
  };

  // Refused for good (a step switched off since the period's series came, the
  // plan): why, once, without a retry that can only fail again — as the block.
  const yearRefusal = year.isError ? seriesRefusal([year.error]) : null;
  const picked = month !== null ? yearSeries?.buckets[month] : undefined;
  const pickedCount = month !== null ? (yearSeries?.counts[month] ?? null) : null;

  return (
    <div className="space-y-2" data-testid="voice-row-trend">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-medium text-fg-muted">{t('voicePage.series.title')}</p>
        <SegmentedButtons
          ariaLabel={t('voicePage.series.viewLabel')}
          options={(['period', 'year'] as const).map((id) => ({
            id,
            label: t(`voicePage.series.view.${id}`),
          }))}
          value={view}
          onChange={changeView}
        />
      </div>

      {view === 'period' && !seriesHasPoints(series) ? (
        <p className="py-4 text-center text-xs text-fg-subtle">
          {t('voicePage.series.periodEmpty')}
        </p>
      ) : view === 'period' ? (
        <VoiceTrendBars
          series={series}
          unit={unit}
          selected={selected}
          onSelect={(index) => onSelect(index === selected ? null : index)}
          period={period}
        />
      ) : year.isPending ? (
        <p className="py-4 text-center text-xs text-fg-subtle">
          {t('voicePage.series.yearLoading')}
        </p>
      ) : year.data && !yearSeries ? (
        // An answer with no line for this row is not a failure: a retry
        // would bring the same answer. A key merged by another version says
        // so; otherwise the year has no data for the row.
        <p className="py-4 text-center text-xs text-fg-subtle">
          {year.data.resolutionStale &&
          (seriesRef.signal === 'competitor' || seriesRef.signal === 'objection')
            ? t(`voicePage.series.stale.${seriesRef.signal}`)
            : t('voicePage.series.yearEmpty')}
        </p>
      ) : yearRefusal ? (
        <p className="py-4 text-center text-xs text-fg-subtle">
          {t(`voicePage.series.unavailable.${yearRefusal}`)}
        </p>
      ) : year.isError || !yearSeries ? (
        <div className="flex items-center justify-center gap-2 py-4 text-xs text-fg-muted">
          <span>{t('voicePage.series.yearFailed')}</span>
          <Button variant="ghost" size="sm" onClick={() => void year.refetch()}>
            {t('voicePage.series.retry')}
          </Button>
        </div>
      ) : (
        <>
          <VoiceTrendBars
            series={yearSeries}
            unit="month"
            selected={month}
            onSelect={(index) =>
              setMonthStart(
                index === month ? null : (yearSeries.buckets[index]?.start ?? null)
              )
            }
            period={period}
          />
          {seriesCoverage(yearSeries.buckets) !== 'full' && (
            <p className="text-xs text-fg-subtle">
              {t('voicePage.series.yearCollecting')}
            </p>
          )}
          {picked && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
              <span>
                {pickedCount === null || picked.calls === null
                  ? t('voicePage.series.barEmpty', { period: period(picked, 'month') })
                  : t('voicePage.series.bar', {
                      period: period(picked, 'month'),
                      count: pickedCount,
                      total: formatNumber(picked.calls),
                      share: bucketShareText(pickedCount, picked.calls),
                    })}
              </span>
              {showMonth && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    showMonth(
                      new Date(
                        (Date.parse(picked.start) + Date.parse(picked.end)) / 2
                      ).toISOString(),
                      monthPatch
                    )
                  }
                >
                  {t('voicePage.series.showMonth')}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
